import React, { useEffect, useMemo, useState } from 'react';
import { propTypes } from './props';
import { ToolBarButtonWrapper } from '../ToolBarButtonWrapper';
import { DropDown } from '../DropDown';
import {
    CustomSizeError,
    CustomSizeField,
    CustomSizeFieldLabel,
    CustomSizeInput,
    CustomSizeInputWrap,
    CustomSizeRow,
    CustomSizeSeparator,
    CustomSizeUnit,
    Label,
    RemoveButton,
    RowContainer,
} from './styled';
import {
    IconBackgroundColor,
    IconBackgroundImage,
    IconBackgroundImageOpacity,
    IconCross,
    IconDropDownSmall,
    IconFrame,
} from '@/assets';
import theme from '@/theme';
import { ColorBox } from '../ColorBox';
import { StyledImage } from '@/generic/Styled';
import { pickImage } from '@/utils';
import { OpacityPicker } from '../OpacityPicker';
import {
    CANVAS_SIZE_MODES,
    CUSTOM_CANVAS_PRESET_KEY,
    pageSizes,
} from '@/constants/layoutConstants';
import { TEXT_DICTIONARY } from '@/constants/textConstants';
import { StrokePicker } from '../StrokePicker';

export const TemplateToolBar = ({
    onSetBackgroundColor,
    canvasBackgroundColor,
    onSetBackgroundImage,
    backgroundImageOpacity,
    onChangeBackgroundImageOpacity,
    onSetPageSize,
    selectedPageSize,
    selectedPageSizeLabel,
    languageLocale,
    uploadImageCallBack,
    setLoadingUploadImage,
    cuttingGuideStroke,
    cuttingGuideStrokeColor,
    onChangeCuttingGuideProp,
    canvasBackgroundImage,
    onRemoveBackgroundImage,
    showBackgroundImagePicker = true,
    showOpacityPicker = true,
}) => {
    const DEFAULT_CUSTOM_MM = 100;
    const [customWidthMm, setCustomWidthMm] = useState(DEFAULT_CUSTOM_MM);
    const [customHeightMm, setCustomHeightMm] = useState(DEFAULT_CUSTOM_MM);
    const [showCustomSizeInputs, setShowCustomSizeInputs] = useState(false);
    const [customSizeError, setCustomSizeError] = useState('');
    const selectedLabel = selectedPageSizeLabel || selectedPageSize?.preset || selectedPageSize;
    const sizeOptions = useMemo(() => [...Object.keys(pageSizes), CUSTOM_CANVAS_PRESET_KEY], []);

    useEffect(() => {
        const isCustom = selectedPageSize?.mode === CANVAS_SIZE_MODES.custom;
        setShowCustomSizeInputs(Boolean(isCustom));
        if (isCustom) {
            setCustomWidthMm(selectedPageSize?.widthMm || DEFAULT_CUSTOM_MM);
            setCustomHeightMm(selectedPageSize?.heightMm || DEFAULT_CUSTOM_MM);
        }
    }, [selectedPageSize]);

    const applyCustomSize = (widthValue = customWidthMm, heightValue = customHeightMm, showError = true) => {
        const widthMm = Number(widthValue);
        const heightMm = Number(heightValue);
        const isValidRange = widthMm >= 10 && widthMm <= 2000 && heightMm >= 10 && heightMm <= 2000;

        if (!Number.isFinite(widthMm) || !Number.isFinite(heightMm) || !isValidRange) {
            if (showError) setCustomSizeError('10-2000 mm');
            return;
        }

        setCustomSizeError('');
        onSetPageSize?.({
            mode: CANVAS_SIZE_MODES.custom,
            widthMm,
            heightMm,
        });
    };
    const handleCustomInputKeyDown = e => {
        if (e.key !== 'Enter') return;
        e.preventDefault();
        applyCustomSize(customWidthMm, customHeightMm, true);
    };

    const handleCustomRowBlur = e => {
        if (e.currentTarget.contains(e.relatedTarget)) return;
        applyCustomSize(customWidthMm, customHeightMm, true);
    };

    const selectBackgroundColor = e => {
        onSetBackgroundColor(e);
    };
    const handleBackgroundUpload = async () => {
        const file = await pickImage();
        if (!file) return;
        if (uploadImageCallBack) {
            setLoadingUploadImage(true);
            const imageUrl = await uploadImageCallBack(file);
            onSetBackgroundImage(imageUrl);
            setLoadingUploadImage(false);
        } else {
            const reader = new FileReader();
            reader.onload = () => {
                onSetBackgroundImage(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    return (
        <RowContainer>
            <DropDown
                gap={20}
                left={<Label>{selectedLabel}</Label>}
                list={sizeOptions}
                onSelect={e => {
                    if (e === CUSTOM_CANVAS_PRESET_KEY) {
                        setShowCustomSizeInputs(true);
                        return;
                    }
                    setCustomSizeError('');
                    setShowCustomSizeInputs(false);
                    onSetPageSize && onSetPageSize(e);
                }}
                tooltip={TEXT_DICTIONARY?.[languageLocale]?.PAGE_SIZE}
            />
            {showCustomSizeInputs ? (
                <CustomSizeRow onBlur={handleCustomRowBlur}>
                    <CustomSizeField>
                        <CustomSizeFieldLabel>Width</CustomSizeFieldLabel>
                        <CustomSizeInputWrap>
                            <CustomSizeInput
                                value={customWidthMm}
                                onChange={e => {
                                    const nextWidth = e.target.value;
                                    setCustomWidthMm(nextWidth);
                                    applyCustomSize(nextWidth, customHeightMm, false);
                                }}
                                onKeyDown={handleCustomInputKeyDown}
                                type="number"
                                min={10}
                                max={2000}
                            />
                            <CustomSizeUnit>mm</CustomSizeUnit>
                        </CustomSizeInputWrap>
                    </CustomSizeField>
                    <CustomSizeSeparator>x</CustomSizeSeparator>
                    <CustomSizeField>
                        <CustomSizeFieldLabel>Height</CustomSizeFieldLabel>
                        <CustomSizeInputWrap>
                            <CustomSizeInput
                                value={customHeightMm}
                                onChange={e => {
                                    const nextHeight = e.target.value;
                                    setCustomHeightMm(nextHeight);
                                    applyCustomSize(customWidthMm, nextHeight, false);
                                }}
                                onKeyDown={handleCustomInputKeyDown}
                                type="number"
                                min={10}
                                max={2000}
                            />
                            <CustomSizeUnit>mm</CustomSizeUnit>
                        </CustomSizeInputWrap>
                    </CustomSizeField>
                    {customSizeError ? <CustomSizeError>{customSizeError}</CustomSizeError> : null}
                </CustomSizeRow>
            ) : null}
            <ToolBarButtonWrapper
                gap={11}
                tooltip={TEXT_DICTIONARY?.[languageLocale]?.BACKGROUND_COLOR}
                tooltipPosition={'bottom'}
            >
                <StyledImage src={IconBackgroundColor} />
                <ColorBox
                    backgroundColor={canvasBackgroundColor || theme.color.white}
                    onChange={selectBackgroundColor}
                />
            </ToolBarButtonWrapper>
            {showBackgroundImagePicker && (
                <ToolBarButtonWrapper
                    paddingRight={17}
                    paddingLeft={17}
                    onClick={handleBackgroundUpload}
                    tooltip={TEXT_DICTIONARY?.[languageLocale]?.BACKGROUND_IMAGE}
                    tooltipPosition={'bottom'}
                >
                    {canvasBackgroundImage && (
                        <RemoveButton
                            onClick={e => {
                                onRemoveBackgroundImage && onRemoveBackgroundImage();
                                e.stopPropagation();
                            }}
                        >
                            <StyledImage src={IconCross} height={15} width={15} />
                        </RemoveButton>
                    )}
                    <StyledImage
                        src={canvasBackgroundImage?.src || IconBackgroundImage}
                        height={24}
                        width={24}
                    />
                </ToolBarButtonWrapper>
            )}
            {showOpacityPicker && (
                <OpacityPicker
                    tooltip={TEXT_DICTIONARY?.[languageLocale]?.BACKGROUND_IMAGE_OPACITY}
                    gap={10}
                    value={backgroundImageOpacity}
                    onChangeOpacity={onChangeBackgroundImageOpacity}
                    leftChild={<StyledImage src={IconBackgroundImageOpacity} />}
                    rightChild={<StyledImage src={IconDropDownSmall} />}
                />
            )}
            <StrokePicker
                gap={10}
                value={cuttingGuideStroke}
                onChangeStrokeWidth={e => {
                    onChangeCuttingGuideProp('cuttingGuideStroke', e);
                }}
                strokeColor={cuttingGuideStrokeColor}
                onChangeShapeStrokeColor={e => {
                    onChangeCuttingGuideProp('cuttingGuideStrokeColor', e);
                }}
                tooltip={TEXT_DICTIONARY?.[languageLocale]?.CUTTING_GUIDE}
                languageLocale={languageLocale}
                leftIcon={IconFrame}
                max={10}
            />
        </RowContainer>
    );
};

TemplateToolBar.propTypes = propTypes;
